/* Analytics event hooks.
 * Every event is pushed to window.dataLayer (for Google Tag Manager, GTM-P3FH5H7G)
 * and, if a gtag() bridge is present, to gtag as well.
 *
 * On top of the raw internal events, key conversions ALSO emit GA4-recommended
 * events (generate_lead, begin_checkout, purchase, contact) with proper
 * ecommerce objects — so GTM triggers / GA4 conversions / Google Ads can be
 * wired up from the dashboard with no further code changes.
 */

const DEFAULT_CURRENCY = "AUD";

function push(obj) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(obj);
}

// Maps our internal event names -> GA4 recommended event configuration.
const GA4_MAP = {
  phone_call_click: { name: "contact", method: "phone" },
  lead_converted: { name: "generate_lead" },
  sample_material_requested: { name: "generate_lead" },
  payment_checkout_started: { name: "begin_checkout", ecommerce: true },
  payment_completed: { name: "purchase", ecommerce: true },
};

function emitGA4(internalEvent, payload) {
  const map = GA4_MAP[internalEvent];
  if (!map) return;

  if (map.ecommerce) {
    const currency = (payload.currency || DEFAULT_CURRENCY).toUpperCase();
    const value = Number(payload.value ?? payload.amount ?? 0) || 0;
    const items = [
      {
        item_id: payload.lookup_key || internalEvent,
        item_name: payload.item_name || payload.lookup_key || "Operations Transformation Program",
        item_category: payload.item_category || "Enrolment",
        price: value,
        quantity: 1,
      },
    ];
    const ecommerce = { currency, value, items };
    if (map.name === "purchase") {
      ecommerce.transaction_id =
        payload.transaction_id || payload.session_id || payload.lookup_key || String(Date.now());
    }
    // GA4 best practice: clear the previous ecommerce object first.
    push({ ecommerce: null });
    push({ event: map.name, ...(payload.provider ? { payment_type: payload.provider } : {}), ecommerce });
  } else {
    push({
      event: map.name,
      ...(map.method ? { method: map.method } : {}),
      ...payload,
    });
  }
}

export function track(event, payload = {}) {
  try {
    // 1) raw internal event (for custom GTM triggers)
    push({ event, ...payload });
    // 2) GA4-recommended mirror event for known conversions
    emitGA4(event, payload);
    // 3) gtag bridge (only if a gtag GA4 tag is present)
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", event, payload);
    }
    // eslint-disable-next-line no-console
    console.debug("[analytics]", event, payload);
  } catch (e) {
    /* no-op */
  }
}

export const EVENTS = {
  HERO_CTA: "hero_cta_click",
  PROSPECTUS_CTA: "prospectus_cta_click",
  CONSULTATION_CTA: "consultation_cta_click",
  MODULE_EXPLORER: "module_explorer_interaction",
  PERSONA_TAB: "persona_tab_interaction",
  FORM_STARTED: "form_started",
  FORM_STEP_COMPLETED: "form_step_completed",
  FORM_SUBMITTED: "form_submitted",
  PROSPECTUS_DOWNLOADED: "prospectus_downloaded",
  CALENDAR_OPENED: "calendar_opened",
  SAMPLE_REQUESTED: "sample_material_requested",
  SAMPLE_DOWNLOADED: "sample_material_downloaded",
  LEAD_CONVERTED: "lead_converted",
  THANK_YOU_VIEW: "thank_you_view",
  PHONE_CALL: "phone_call_click",
  CHECKOUT_STARTED: "payment_checkout_started",
  PURCHASE: "payment_completed",
};
